import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { ChartEmpty } from '../components/ChartEmpty'
import { DomainPageShell } from '../components/DomainPageShell'
import { KpiCard } from '../components/KpiCard'
import { AnaliseMl, AnaliseShopee } from './reclamacoes/Analise'
import { carregarHistorico, carregarReclamacoes, type Fonte } from './reclamacoes/fonte'
import { lerRecorte } from './reclamacoes/insights'
import { useRegisterPanelInsights } from '../state/PanelInsightsContext'
import {
  ETIQUETA_PRIORIDADE,
  prioridade,
  sugerir,
  sugerirShopee,
  type Sugestao,
  type SugestaoShopee,
} from './reclamacoes/sugestao'
import { pedirRascunhoMl, pedirRascunhoShopee } from './reclamacoes/rascunhoIa'
import {
  MOTIVO_SHOPEE_CURTO,
  NOME_CONTA_ML,
  SITUACAO_SHOPEE,
  brl0,
  brl2,
  diaCurto,
  motivosDisponiveis,
  type CasoMl,
  type ContaMl,
  type DadosMl,
  type DadosShopee,
  type FilaShopee,
} from './reclamacoes/logic'

type Canal = 'ml' | 'shopee'

/** Recortes da fila — o que a analista precisa tocar, não o histórico inteiro. */
type Recorte = 'tratar' | 'perdidas' | 'faltando' | 'todas'

const RECORTES: Array<{ id: Recorte; label: string; ajuda: string }> = [
  { id: 'tratar', label: 'A tratar', ajuda: 'casos ainda abertos hoje' },
  { id: 'perdidas', label: 'Janela perdida', ajuda: 'prazo do mediador venceu sem resposta' },
  { id: 'faltando', label: 'Item faltando', ajuda: 'onde o valor do reembolso está em disputa' },
  { id: 'todas', label: 'Todas', ajuda: 'histórico completo do período' },
]

const ITEM_FALTANDO = new Set(['PDD9952', 'PDD9955'])

/** Vista dentro do canal: trabalhar a fila ou olhar o conjunto. */
type Vista = 'fila' | 'analise'

/** Atalhos de período, contados a partir do fim da leitura (não de hoje). */
const ATALHOS_PERIODO: Array<{ label: string; dias: number | null }> = [
  { label: '7 dias', dias: 6 },
  { label: '30 dias', dias: 29 },
  { label: 'tudo', dias: null },
]

const recuar = (iso: string, dias: number): string => {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() - dias)
  return d.toISOString().slice(0, 10)
}

/**
 * A fila rola; os cards não encolhem.
 *
 * Num Stack (flex column) com altura limitada, o padrão do flexbox é espremer os
 * filhos (`flex-shrink: 1`) antes de rolar — com fila cheia os cards achatavam e
 * o texto sumia. `flexShrink: 0` + altura mínima garantem que cada caso ocupe o
 * espaço que precisa e o excedente vire rolagem.
 */
const FILA_SX = {
  maxHeight: { xs: 460, md: 620, lg: 720 },
  minHeight: 220,
  overflowY: 'auto',
  pr: 0.5,
  '& > *': { flexShrink: 0, minHeight: 76 },
  // barra discreta, mas visível — a fila precisa parecer rolável
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { width: 8 },
  '&::-webkit-scrollbar-thumb': { borderRadius: 8, bgcolor: 'action.disabled' },
} as const

export function ReclamacoesPage() {
  const theme = useTheme()

  const [ml, setMl] = useState<DadosMl | null>(null)
  const [shopee, setShopee] = useState<DadosShopee | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fonte, setFonte] = useState<Fonte>('arquivo')
  const [motivoFallback, setMotivoFallback] = useState<string | null>(null)
  const [origem, setOrigem] = useState<'api' | 'banco' | 'api+banco' | null>(null)
  const [avisos, setAvisos] = useState<string[]>([])
  /** API ao vivo em segundo plano — a tela já mostrou o banco */
  const [atualizandoAoVivo, setAtualizandoAoVivo] = useState(false)

  // histórico de 60 dias, carregado só quando a aba de análise abre
  const [histMl, setHistMl] = useState<DadosMl | null>(null)
  const [histShopee, setHistShopee] = useState<DadosShopee | null>(null)
  const [histFonte, setHistFonte] = useState<string | null>(null)
  const [carregandoHist, setCarregandoHist] = useState(false)
  const [copiado, setCopiado] = useState(false)

  // canal na URL: dá link direto para uma aba (/reclamacoes?canal=shopee)
  const [params, setParams] = useSearchParams()
  const canal: Canal = params.get('canal') === 'shopee' ? 'shopee' : 'ml'
  const setCanal = (v: Canal) => {
    const p = new URLSearchParams(params)
    if (v === 'ml') p.delete('canal')
    else p.set('canal', v)
    setParams(p, { replace: true })
  }

  // vista dentro do canal: a fila de trabalho ou a análise do período
  const vista: Vista = params.get('vista') === 'analise' ? 'analise' : 'fila'
  const setVista = (v: Vista) => {
    const p = new URLSearchParams(params)
    if (v === 'fila') p.delete('vista')
    else p.set('vista', v)
    setParams(p, { replace: true })
  }

  // filtros da fila ML
  const [recorte, setRecorte] = useState<Recorte>('tratar')
  const [conta, setConta] = useState<ContaMl | 'all'>('all')
  const [motivo, setMotivo] = useState('all')
  const [busca, setBusca] = useState('')
  const [selecionado, setSelecionado] = useState<string | null>(null)

  // período — vale para a fila e para a análise
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')

  // filtros da fila Shopee
  const [lojaShopee, setLojaShopee] = useState<'all' | 'L1' | 'L2'>('all')
  const [soPendentes, setSoPendentes] = useState(false)
  const [selecionadoSh, setSelecionadoSh] = useState<string | null>(null)

  const aplicarDados = (dados: Awaited<ReturnType<typeof carregarReclamacoes>>) => {
    setMl(dados.ml)
    setShopee(dados.shopee)
    setFonte(dados.fonte)
    setMotivoFallback(dados.motivoFallback || null)
    setOrigem(dados.origem || null)
    setAvisos(dados.avisos || [])
    // o período abre no que a fonte cobre — nunca num intervalo sem dado
    setDe((atual) => atual || dados.ml.meta.periodo.de || '')
    setAte((atual) => atual || dados.ml.meta.periodo.ate || '')
  }

  /**
   * Banco primeiro (rápido), API ao vivo em segundo plano.
   *
   * A analista precisa ver a fila já; o prazo do mediador justifica refrescar
   * o ao vivo, mas não justifica bloquear a tela 1–2 min a cada entrada.
   */
  const load = () => {
    setLoading(true)
    setError(null)
    setAtualizandoAoVivo(false)
    carregarReclamacoes(60, 'banco')
      .then((dados) => {
        aplicarDados(dados)
        setLoading(false)
        setAtualizandoAoVivo(true)
        return carregarReclamacoes(60, 'api')
          .then((aoVivo) => {
            // só troca se veio da API de verdade (não outro fallback de banco/arquivo)
            if (aoVivo.fonte === 'bridge' && (aoVivo.origem === 'api' || aoVivo.origem === 'api+banco')) {
              aplicarDados(aoVivo)
            }
          })
          .catch(() => {
            /* mantém o banco já em tela */
          })
          .finally(() => setAtualizandoAoVivo(false))
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Falha ao carregar a Central de Reclamações')
        setLoading(false)
      })
  }

  useEffect(load, [])

  /**
   * A análise lê o histórico gravado (60 dias), não a fila.
   *
   * São dados diferentes de propósito: a fila é o agora, vindo da API; a
   * análise é o acumulado, que já não muda. Carrega só quando a aba abre.
   */
  useEffect(() => {
    if (vista !== 'analise' || histMl || carregandoHist) return
    setCarregandoHist(true)
    carregarHistorico(60)
      .then((dados) => {
        setHistMl(dados.ml)
        setHistShopee(dados.shopee)
        setHistFonte(dados.motivoFallback || null)
      })
      .catch(() => setHistFonte('não foi possível carregar o histórico'))
      .finally(() => setCarregandoHist(false))
  }, [vista, histMl, carregandoHist])

  // ---------------------------------------------------------------- ML

  /** Base da fila: o que veio da API, recortado por período, conta e motivo. */
  const casosPeriodo = useMemo(() => {
    if (!ml) return []
    return ml.casos.filter((c) => {
      if (de && c.data < de) return false
      if (ate && c.data > ate) return false
      if (conta !== 'all' && c.conta !== conta) return false
      if (motivo !== 'all' && c.motivoLabel !== motivo) return false
      return true
    })
  }, [ml, de, ate, conta, motivo])

  /** Base da análise: o histórico gravado, no mesmo recorte de período. */
  const casosAnalise = useMemo(() => {
    const fonteCasos = histMl?.casos || ml?.casos || []
    return fonteCasos.filter((c) => {
      if (de && c.data < de) return false
      if (ate && c.data > ate) return false
      if (conta !== 'all' && c.conta !== conta) return false
      if (motivo !== 'all' && c.motivoLabel !== motivo) return false
      return true
    })
  }, [histMl, ml, de, ate, conta, motivo])

  const fila = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    return casosPeriodo
      .filter((c) => {
        if (recorte === 'tratar' && !c.aberta) return false
        if (recorte === 'perdidas' && !(c.temPrazo && c.noPrazo === 0)) return false
        if (recorte === 'faltando' && !ITEM_FALTANDO.has(c.motivoCod)) return false
        if (termo) {
          const alvo = `${c.id} ${c.mlb || ''} ${c.itemTitulo || ''} ${c.motivoLabel}`.toLowerCase()
          if (!alvo.includes(termo)) return false
        }
        return true
      })
      .sort((a, b) => prioridade(a) - prioridade(b) || (a.data < b.data ? 1 : -1))
  }, [casosPeriodo, recorte, busca])

  const casoAtual: CasoMl | null = useMemo(
    () => fila.find((c) => c.id === selecionado) || fila[0] || null,
    [fila, selecionado],
  )
  const sugestao: Sugestao | null = useMemo(() => (casoAtual ? sugerir(casoAtual) : null), [casoAtual])

  /** Texto a copiar: regras de imediato; LLM refina em background (E.1). */
  const [textoMl, setTextoMl] = useState<string | null>(null)
  const [fonteTextoMl, setFonteTextoMl] = useState<'regras' | 'llm'>('regras')
  const [gerandoMl, setGerandoMl] = useState(false)

  useEffect(() => {
    if (!casoAtual || !sugestao) {
      setTextoMl(null)
      setFonteTextoMl('regras')
      setGerandoMl(false)
      return
    }
    setTextoMl(sugestao.texto)
    setFonteTextoMl('regras')
    if (!sugestao.texto || sugestao.conduta === 'nao_responder') {
      setGerandoMl(false)
      return
    }
    let cancelado = false
    setGerandoMl(true)
    pedirRascunhoMl(casoAtual, sugestao)
      .then((r) => {
        if (cancelado) return
        if (r.ok && r.texto) {
          setTextoMl(r.texto)
          setFonteTextoMl(r.fonte === 'llm' ? 'llm' : 'regras')
        }
      })
      .catch(() => {
        /* mantém regras */
      })
      .finally(() => {
        if (!cancelado) setGerandoMl(false)
      })
    return () => {
      cancelado = true
    }
  }, [casoAtual, sugestao])

  // na análise a leitura é do período inteiro; na fila, do que está em tela
  const insights = useMemo(
    () => lerRecorte(vista === 'analise' ? casosAnalise : fila),
    [vista, casosAnalise, fila],
  )

  // sem LLM aqui: a leitura é o motor de regras sobre os casos em tela
  useRegisterPanelInsights({
    titulo: 'Reclamações',
    verdade: 'Leitura recalculada a cada filtro, a partir dos casos em tela (regras, sem IA).',
    sugestoes: insights,
    aceitaFoco: false,
  })

  const kpis = useMemo(() => {
    if (!ml) return null
    const abertas = ml.casos.filter((c) => c.aberta)
    const perdidas = ml.casos.filter((c) => c.temPrazo && c.noPrazo === 0)
    const faltando = abertas.filter((c) => ITEM_FALTANDO.has(c.motivoCod))
    return {
      abertas: abertas.length,
      perdidas: perdidas.length,
      faltando: faltando.length,
      valor: abertas.reduce((s, c) => s + (c.valor || 0), 0),
    }
  }, [ml])

  // ---------------------------------------------------------------- Shopee

  const filaSh = useMemo(() => {
    if (!shopee) return []
    return shopee.fila
      .filter((r) => (lojaShopee === 'all' || r.lojaCod === lojaShopee) && (!soPendentes || r.pendente))
      .slice()
      .sort((a, b) => Number(b.pendente) - Number(a.pendente) || b.valor - a.valor)
  }, [shopee, lojaShopee, soPendentes])

  const casoSh: FilaShopee | null = useMemo(
    () => filaSh.find((r) => r.sn === selecionadoSh) || filaSh[0] || null,
    [filaSh, selecionadoSh],
  )
  const sugestaoSh: SugestaoShopee | null = useMemo(
    () => (casoSh ? sugerirShopee(casoSh) : null),
    [casoSh],
  )

  const [textoSh, setTextoSh] = useState<string | null>(null)
  const [fonteTextoSh, setFonteTextoSh] = useState<'regras' | 'llm'>('regras')
  const [gerandoSh, setGerandoSh] = useState(false)

  useEffect(() => {
    if (!casoSh || !sugestaoSh) {
      setTextoSh(null)
      setFonteTextoSh('regras')
      setGerandoSh(false)
      return
    }
    setTextoSh(sugestaoSh.texto)
    setFonteTextoSh('regras')
    if (!sugestaoSh.texto || sugestaoSh.conduta === 'nao_disputar') {
      setGerandoSh(false)
      return
    }
    let cancelado = false
    setGerandoSh(true)
    pedirRascunhoShopee(casoSh, sugestaoSh)
      .then((r) => {
        if (cancelado) return
        if (r.ok && r.texto) {
          setTextoSh(r.texto)
          setFonteTextoSh(r.fonte === 'llm' ? 'llm' : 'regras')
        }
      })
      .catch(() => {
        /* mantém regras */
      })
      .finally(() => {
        if (!cancelado) setGerandoSh(false)
      })
    return () => {
      cancelado = true
    }
  }, [casoSh, sugestaoSh])

  const copiar = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(true)
    } catch {
      setCopiado(false)
    }
  }

  const motivos = ml ? motivosDisponiveis(ml.casos) : []

  return (
    <DomainPageShell
      title="Central de Reclamações"
      subtitle="Fila de casos a tratar, com sugestão de resposta caso a caso"
      loading={false}
      error={error}
      onRetry={load}
    >
      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', gap: 1.5, py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            A carregar o retrato gravado…
          </Typography>
        </Box>
      ) : null}

      {!loading && !ml ? <ChartEmpty title="Nenhum dado carregado." suggestPeriod={false} /> : null}

      {!loading && ml && shopee && kpis ? (
        <Stack spacing={2.5}>
          {/* a tela nunca esconde a idade do dado nem de onde ele veio */}
          {fonte === 'bridge' ? (
            <Alert
              severity={atualizandoAoVivo || origem === 'banco' ? 'info' : 'success'}
              variant="outlined"
              icon={atualizandoAoVivo ? <CircularProgress size={18} /> : undefined}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                {atualizandoAoVivo
                  ? 'Retrato do banco — atualizando ao vivo em segundo plano'
                  : origem === 'banco'
                    ? 'Retrato gravado no banco'
                    : 'Consultado agora nos marketplaces'}
              </AlertTitle>
              {atualizandoAoVivo
                ? 'Pode trabalhar já com estes casos. Em 1–2 minutos a fila atualiza com o estado mais recente do ML e da Shopee.'
                : origem === 'banco'
                  ? 'A fila veio do histórico gravado. Casos podem ter mudado de estado desde a última coleta.'
                  : 'A fila vem direto da API do Mercado Livre e da Shopee, no momento em que a tela abriu.'}
              {avisos.length ? (
                <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
                  {avisos.map((a, i) => (
                    <Typography key={i} component="li" variant="caption">
                      {a}
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </Alert>
          ) : (
            <Alert severity="warning" variant="outlined">
              <AlertTitle sx={{ fontWeight: 700 }}>
                Leitura de {diaCurto(ml.meta.periodo.ate)}, de arquivo — não é tempo real
              </AlertTitle>
              Mercado Livre coletado em {ml.meta.coletado_em.split('-').reverse().join('/')} · Shopee
              em {shopee.meta.coletado_em.split('-').reverse().join('/')}.{' '}
              {motivoFallback
                ? `A tela tentou o endpoint do coletor e voltou para o arquivo: ${motivoFallback}.`
                : 'A tela está lendo o arquivo da última leitura manual.'}
            </Alert>
          )}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
          >
            <Tabs value={canal} onChange={(_, v: Canal) => setCanal(v)}>
              <Tab value="ml" label={`Mercado Livre (${kpis.abertas} a tratar)`} />
              <Tab
                value="shopee"
                label={`Shopee (${shopee.fila.filter((r) => r.pendente).length} a tratar)`}
              />
            </Tabs>
            <Stack direction="row" spacing={0.75}>
              {(
                [
                  ['fila', 'Fila de casos'],
                  ['analise', 'Análise'],
                ] as Array<[Vista, string]>
              ).map(([v, label]) => (
                <Chip
                  key={v}
                  size="small"
                  label={label}
                  color={vista === v ? 'primary' : 'default'}
                  variant={vista === v ? 'filled' : 'outlined'}
                  onClick={() => setVista(v)}
                />
              ))}
            </Stack>
          </Stack>

          {canal === 'ml' ? (
            <>
              <Grid container spacing={{ xs: 1.25, sm: 2 }}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <KpiCard label="Casos a tratar" value={String(kpis.abertas)} hint="ainda abertos hoje" />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <KpiCard
                    label="Janelas perdidas"
                    value={String(kpis.perdidas)}
                    hint="prazo do mediador venceu sem resposta"
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <KpiCard
                    label="Item faltando em aberto"
                    value={String(kpis.faltando)}
                    hint="onde o valor do reembolso está em disputa"
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <KpiCard label="Valor em jogo" value={brl0(kpis.valor)} hint="soma dos pedidos em aberto" />
                </Grid>
              </Grid>

              {/* ---------------- filtros ---------------- */}
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    {vista === 'fila' ? (
                      <>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {RECORTES.map((r) => (
                            <Chip
                              key={r.id}
                              size="small"
                              label={r.label}
                              title={r.ajuda}
                              color={recorte === r.id ? 'primary' : 'default'}
                              variant={recorte === r.id ? 'filled' : 'outlined'}
                              onClick={() => {
                                setRecorte(r.id)
                                setSelecionado(null)
                              }}
                            />
                          ))}
                        </Stack>
                        <Divider orientation="vertical" flexItem />
                      </>
                    ) : null}
                    <TextField
                      label="De"
                      type="date"
                      size="small"
                      value={de}
                      onChange={(e) => setDe(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <TextField
                      label="Até"
                      type="date"
                      size="small"
                      value={ate}
                      onChange={(e) => setAte(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <Stack direction="row" spacing={0.75}>
                      {ATALHOS_PERIODO.map((a) => (
                        <Chip
                          key={a.label}
                          size="small"
                          variant="outlined"
                          label={a.label}
                          onClick={() => {
                            const fim = ml.meta.periodo.ate
                            setDe(a.dias === null ? ml.meta.periodo.de : recuar(fim, a.dias))
                            setAte(fim)
                          }}
                        />
                      ))}
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack direction="row" spacing={0.75}>
                      {(['all', 'F', 'H'] as const).map((c) => (
                        <Chip
                          key={c}
                          size="small"
                          label={c === 'all' ? 'As duas contas' : NOME_CONTA_ML[c]}
                          color={conta === c ? 'primary' : 'default'}
                          variant={conta === c ? 'filled' : 'outlined'}
                          onClick={() => setConta(c)}
                        />
                      ))}
                    </Stack>
                    <TextField
                      label="Motivo"
                      select
                      size="small"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      sx={{ minWidth: 210 }}
                    >
                      <MenuItem value="all">Todos os motivos</MenuItem>
                      {motivos.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      placeholder="reclamação, MLB ou produto"
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      sx={{ minWidth: 240 }}
                      slotProps={{
                        input: {
                          startAdornment: <InputAdornment position="start">🔎</InputAdornment>,
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {vista === 'fila'
                        ? `${fila.length} caso${fila.length === 1 ? '' : 's'} nesse recorte`
                        : `${casosAnalise.length} reclamaç${casosAnalise.length === 1 ? 'ão' : 'ões'} no histórico do período`}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>

              {vista === 'analise' ? (
                <>
                  {carregandoHist ? (
                    <Typography variant="caption" color="text.secondary">
                      carregando o histórico de 60 dias…
                    </Typography>
                  ) : null}
                  {histFonte ? (
                    <Alert severity="warning" variant="outlined">
                      A análise está lendo dado de arquivo: {histFonte}.
                    </Alert>
                  ) : null}
                  <AnaliseMl casos={casosAnalise} />
                </>
              ) : null}

              <Grid container spacing={2} sx={{ display: vista === 'fila' ? undefined : 'none' }}>
                {/* ---------------- fila ---------------- */}
                <Grid size={{ xs: 12, lg: 5 }}>
                  <Stack spacing={1} sx={FILA_SX}>
                    {fila.length ? (
                      fila.map((c) => {
                        const et = ETIQUETA_PRIORIDADE[prioridade(c)]
                        const ativo = casoAtual?.id === c.id
                        return (
                          <Card
                            key={c.id}
                            sx={{
                              borderLeft: '4px solid',
                              borderColor: ativo ? 'primary.main' : 'transparent',
                              bgcolor: ativo ? alpha(theme.palette.primary.main, 0.05) : undefined,
                            }}
                          >
                            <CardActionArea onClick={() => setSelecionado(c.id)}>
                              <CardContent sx={{ py: 1.25 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  spacing={1}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                      {c.itemTitulo || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {c.motivoLabel} · {diaCurto(c.data)} · {NOME_CONTA_ML[c.conta]}
                                    </Typography>
                                  </Box>
                                  <Stack spacing={0.5} alignItems="flex-end">
                                    <Typography
                                      variant="caption"
                                      sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                                    >
                                      {c.valor ? brl2(c.valor) : '—'}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={et.label}
                                      color={et.tom}
                                      variant={et.tom === 'default' ? 'outlined' : 'filled'}
                                    />
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        )
                      })
                    ) : (
                      <ChartEmpty title="Nenhum caso nesse recorte." suggestPeriod={false} dense />
                    )}
                  </Stack>
                </Grid>

                {/* ---------------- detalhe + sugestão ---------------- */}
                <Grid size={{ xs: 12, lg: 7 }}>
                  {casoAtual && sugestao ? (
                    <Card sx={{ position: { lg: 'sticky' }, top: 16 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                          {casoAtual.itemTitulo || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                          Reclamação {casoAtual.id} · {casoAtual.mlb} · conta{' '}
                          {NOME_CONTA_ML[casoAtual.conta]} · aberta em {diaCurto(casoAtual.data)} ·{' '}
                          {casoAtual.valor ? brl2(casoAtual.valor) : 'valor não informado'}
                        </Typography>

                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                          <Chip
                            size="small"
                            label={sugestao.condutaLabel}
                            color={
                              sugestao.conduta === 'nao_responder'
                                ? 'default'
                                : sugestao.conduta === 'resolver'
                                  ? 'warning'
                                  : 'primary'
                            }
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={sugestao.chanceLabel}
                            color={sugestao.chance === 'baixa' ? 'error' : 'default'}
                          />
                          <Chip size="small" variant="outlined" label={casoAtual.motivoLabel} />
                          {casoAtual.temPrazo ? (
                            <Chip
                              size="small"
                              color={casoAtual.noPrazo === 0 ? 'error' : 'success'}
                              label={casoAtual.noPrazo === 0 ? 'janela perdida' : 'respondida no prazo'}
                            />
                          ) : null}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              casoAtual.mensagens === 1
                                ? '1 mensagem'
                                : `${casoAtual.mensagens} mensagens`
                            }
                          />
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {sugestao.porque}
                        </Typography>

                        {textoMl ? (
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                              mb: 1.25,
                            }}
                          >
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                              <Chip
                                size="small"
                                label={fonteTextoMl === 'llm' ? 'Proposta da IA' : 'Modelo (regras)'}
                                color={fonteTextoMl === 'llm' ? 'primary' : 'default'}
                                variant="outlined"
                              />
                              {gerandoMl ? (
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <CircularProgress size={12} />
                                  <Typography variant="caption" color="text.secondary">
                                    a refinar…
                                  </Typography>
                                </Stack>
                              ) : null}
                            </Stack>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {textoMl}
                            </Typography>
                          </Box>
                        ) : (
                          <Alert severity="success" variant="outlined" sx={{ mb: 1.25 }}>
                            Não há mensagem a enviar neste caso — a conduta recomendada é não responder.
                          </Alert>
                        )}

                        {textoMl ? (
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{ textTransform: 'none' }}
                              onClick={() => copiar(textoMl)}
                            >
                              Copiar resposta
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              revise e complete [colchetes] antes de colar — nada é enviado por esta tela
                            </Typography>
                          </Stack>
                        ) : null}

                        {sugestao.avisos.length ? (
                          <Box>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">
                              Antes de escrever
                            </Typography>
                            <Stack component="ul" spacing={0.5} sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                              {sugestao.avisos.map((a, i) => (
                                <Typography key={i} component="li" variant="caption" color="text.secondary">
                                  {a}
                                </Typography>
                              ))}
                            </Stack>
                          </Box>
                        ) : null}

                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary">
                          Base: {sugestao.base}. Modelo a revisar — nenhuma mensagem é enviada por esta tela.
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : null}
                </Grid>
              </Grid>

              {/* ---------------- leitura automática ---------------- */}
              {insights.length ? (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
                    <Typography variant="h6">Leitura do recorte</Typography>
                    <Typography variant="caption" color="text.secondary">
                      recalculada a cada filtro, a partir dos casos em tela
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {insights.map((i) => (
                      <Grid key={i.titulo} size={{ xs: 12, md: 6 }}>
                        <Card
                          sx={{
                            height: '100%',
                            borderLeft: '4px solid',
                            borderColor:
                              i.tom === 'critico'
                                ? 'error.main'
                                : i.tom === 'atencao'
                                  ? 'warning.main'
                                  : 'divider',
                          }}
                        >
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3, mb: 0.5 }}>
                              {i.titulo}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {i.detalhe}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : null}
            </>
          ) : (
            /* ================================ SHOPEE ================================ */
            <>
              <Grid container spacing={{ xs: 1.25, sm: 2 }}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <KpiCard
                    label="Devoluções vivas"
                    value={String(shopee.fila.length)}
                    hint="ainda sem desfecho"
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <KpiCard
                    label="Esperando resposta"
                    value={String(shopee.fila.filter((r) => r.pendente).length)}
                    hint="a taxa de resposta segura a nota da loja"
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <KpiCard
                    label="Valor na fila"
                    value={brl0(shopee.fila.reduce((s, r) => s + r.valor, 0))}
                    hint="soma das devoluções vivas"
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <KpiCard
                    label="Canceladas sozinhas"
                    value={String(
                      shopee.motivos.reduce((s, m) => s + m.festa.canceladas + m.decora.canceladas, 0),
                    )}
                    hint={`de ${shopee.meta.totais_declarados.devolucoes} no período, sem ação da equipe`}
                  />
                </Grid>
              </Grid>

              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Stack direction="row" spacing={0.75}>
                      {(
                        [
                          ['all', 'As duas lojas'],
                          ['L1', 'FESTA'],
                          ['L2', 'DECORA'],
                        ] as Array<['all' | 'L1' | 'L2', string]>
                      ).map(([v, label]) => (
                        <Chip
                          key={v}
                          size="small"
                          label={label}
                          color={lojaShopee === v ? 'primary' : 'default'}
                          variant={lojaShopee === v ? 'filled' : 'outlined'}
                          onClick={() => setLojaShopee(v)}
                        />
                      ))}
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Chip
                      size="small"
                      label="Só quem espera resposta"
                      color={soPendentes ? 'warning' : 'default'}
                      variant={soPendentes ? 'filled' : 'outlined'}
                      onClick={() => setSoPendentes((v) => !v)}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {filaSh.length} caso{filaSh.length === 1 ? '' : 's'}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>

              {vista === 'analise' ? (
                <AnaliseShopee dados={histShopee || shopee} loja={lojaShopee} onLoja={setLojaShopee} />
              ) : null}

              <Grid container spacing={2} sx={{ display: vista === 'fila' ? undefined : 'none' }}>
                <Grid size={{ xs: 12, lg: 5 }}>
                  <Stack spacing={1} sx={FILA_SX}>
                    {filaSh.length ? (
                      filaSh.map((r) => {
                        const sit = SITUACAO_SHOPEE[r.status] || { label: r.status, tom: 'neutro' as const }
                        const ativo = casoSh?.sn === r.sn
                        return (
                          <Card
                            key={r.sn}
                            sx={{
                              borderLeft: '4px solid',
                              borderColor: ativo ? 'primary.main' : 'transparent',
                              bgcolor: ativo ? alpha(theme.palette.primary.main, 0.05) : undefined,
                            }}
                          >
                            <CardActionArea onClick={() => setSelecionadoSh(r.sn)}>
                              <CardContent sx={{ py: 1.25 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  spacing={1}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                      {r.produto}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {MOTIVO_SHOPEE_CURTO[r.motivoCod] || r.motivoCod} · {r.data} · {r.loja}
                                    </Typography>
                                  </Box>
                                  <Stack spacing={0.5} alignItems="flex-end">
                                    <Typography
                                      variant="caption"
                                      sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                                    >
                                      {brl2(r.valor)}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={r.pendente ? 'responder' : sit.label}
                                      color={r.pendente ? 'warning' : sit.tom === 'crit' ? 'error' : 'default'}
                                      variant={r.pendente || sit.tom === 'crit' ? 'filled' : 'outlined'}
                                    />
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        )
                      })
                    ) : (
                      <ChartEmpty title="Nenhuma devolução nesse recorte." suggestPeriod={false} dense />
                    )}
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 7 }}>
                  {casoSh && sugestaoSh ? (
                    <Card sx={{ position: { lg: 'sticky' }, top: 16 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                          {casoSh.produto}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                          Devolução {casoSh.sn} · loja {casoSh.loja} · aberta em {casoSh.data} ·{' '}
                          {brl2(casoSh.valor)}
                        </Typography>

                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                          <Chip
                            size="small"
                            label={sugestaoSh.condutaLabel}
                            color={sugestaoSh.conduta === 'nao_disputar' ? 'default' : 'primary'}
                          />
                          {sugestaoSh.tne ? (
                            <Chip size="small" color="error" label="entra na Taxa de Não Envio" />
                          ) : (
                            <Chip size="small" variant="outlined" label="fora da Taxa de Não Envio" />
                          )}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={MOTIVO_SHOPEE_CURTO[casoSh.motivoCod] || casoSh.motivoCod}
                          />
                        </Stack>

                        {casoSh.texto ? (
                          <Box
                            sx={{
                              borderLeft: '3px solid',
                              borderColor: 'divider',
                              pl: 1.25,
                              py: 0.25,
                              mb: 1.5,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" display="block">
                              o que o comprador escreveu
                            </Typography>
                            <Typography variant="body2" fontStyle="italic">
                              {casoSh.texto}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                            O comprador não escreveu motivo — só marcou a opção.
                          </Typography>
                        )}

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {sugestaoSh.porque}
                        </Typography>

                        {textoSh ? (
                          <>
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                                mb: 1.25,
                              }}
                            >
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                                <Chip
                                  size="small"
                                  label={fonteTextoSh === 'llm' ? 'Proposta da IA' : 'Modelo (regras)'}
                                  color={fonteTextoSh === 'llm' ? 'primary' : 'default'}
                                  variant="outlined"
                                />
                                {gerandoSh ? (
                                  <Stack direction="row" spacing={0.75} alignItems="center">
                                    <CircularProgress size={12} />
                                    <Typography variant="caption" color="text.secondary">
                                      a refinar…
                                    </Typography>
                                  </Stack>
                                ) : null}
                              </Stack>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {textoSh}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ textTransform: 'none' }}
                                onClick={() => copiar(textoSh)}
                              >
                                Copiar resposta
                              </Button>
                              <Typography variant="caption" color="text.secondary">
                                revise e complete [colchetes] — só pela Central do Vendedor
                              </Typography>
                            </Stack>
                          </>
                        ) : null}

                        {sugestaoSh.avisos.length ? (
                          <Box>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">
                              Antes de agir
                            </Typography>
                            <Stack component="ul" spacing={0.5} sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                              {sugestaoSh.avisos.map((a, i) => (
                                <Typography key={i} component="li" variant="caption" color="text.secondary">
                                  {a}
                                </Typography>
                              ))}
                            </Stack>
                          </Box>
                        ) : null}

                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary">
                          Base: {sugestaoSh.base}. Toda proposta sai pela Central do Vendedor — nada por fora
                          da plataforma.
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : null}
                </Grid>
              </Grid>
            </>
          )}
        </Stack>
      ) : null}

      <Snackbar
        open={copiado}
        autoHideDuration={2000}
        onClose={() => setCopiado(false)}
        message="Resposta copiada — revise os campos entre colchetes antes de colar"
      />
    </DomainPageShell>
  )
}
